"use client";

import { type MouseEvent, useMemo, useState } from "react";
import Link from "next/link";
import {
  ArrowUpRight,
  Gamepad2,
  Globe2,
  MapPin,
  Search,
  X,
} from "lucide-react";
import * as CountryFlags from "country-flag-icons/react/3x2";
import { getBackendAssetUrl } from "@/lib/api-config";
import { getRegionDisplayName, isSingaporeRegion } from "@/lib/regions";
import { RegionIcons } from "./RegionIcons";
export { isSingaporeRegion, RegionIcons };

const catalogImageUrl = (url?: string) => {
  if (!url) return "";
  if (
    url.startsWith("/images/") ||
    url.startsWith("/assets/") ||
    url.startsWith("/uploads/")
  ) {
    return url;
  }
  return getBackendAssetUrl(url);
};

export type CatalogGameSummary = {
  id: string;
  name: string;
  slug: string;
  category?: string;
  shortDescription?: string;
  imageUrl?: string;
  imageAlt?: string;
  regions?: Array<{
    id: string;
    name: string;
    slug: string;
    code: string;
    isDefault?: boolean;
    isIndexed?: boolean;
  }>;
  _count?: { products?: number; blogLinks?: number };
};

const isCombinedFreeFireRegion = (
  game: CatalogGameSummary,
  region: NonNullable<CatalogGameSummary["regions"]>[number],
) => {
  if (game.slug !== "free-fire") return false;
  const code = (region.code || "").toUpperCase();
  return (
    code === "MY/SG" ||
    code === "SG/MY" ||
    code === "SG/MY/LK" ||
    (code === "SG" &&
      !(game.regions || []).some(
        (item) => (item.code || "").toUpperCase() === "MY/SG",
      ))
  );
};

export const validRegions = (game: CatalogGameSummary) => {
  const regions = (game.regions || []).filter(
    (region) =>
      region.name?.trim() && region.slug?.trim() && region.code?.trim(),
  );

  const hasCombinedRegion = regions.some(
    (region) => region.code.toUpperCase() === "MY/SG",
  );

  return regions
    .filter((region) => {
      if (game.slug === "free-fire") {
        return (
          region.code.toUpperCase() !== "MY" &&
          (region.code.toUpperCase() !== "SG" || !hasCombinedRegion)
        );
      }
      return true;
    })
    .sort((left, right) => {
      if (game.slug === "free-fire") {
        const leftCombined = isCombinedFreeFireRegion(game, left);
        const rightCombined = isCombinedFreeFireRegion(game, right);
        if (leftCombined && !rightCombined) return -1;
        if (!leftCombined && rightCombined) return 1;
      }
      const leftSg = isSingaporeRegion(left);
      const rightSg = isSingaporeRegion(right);
      if (leftSg && !rightSg) return -1;
      if (!leftSg && rightSg) return 1;
      return 0;
    });
};

const gameCategories = [
  { key: "all", label: "All" },
  { key: "pc-games", label: "PC Games" },
  { key: "mobile-games", label: "Mobile Games" },
  { key: "gift-card", label: "Gift Card" },
  { key: "live-app", label: "Apps & Social" },
] as const;

const featuredGameOrder = [
  "free-fire",
  "pubg-mobile",
  "mobile-legend",
  "blood-strike",
  "valorant-pc",
  "call-of-duty-mobile-garena",
  "8-ball-pool",
  "asphalt-9-legends",
] as const;

const featuredGameRank = new Map<string, number>(
  featuredGameOrder.map((slug, index) => [slug, index]),
);

const categoryKey = (value?: string) => {
  const key = (value || "mobile-games")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  if (["apps-social", "apps-and-social", "live-app"].includes(key))
    return "live-app";
  if (["gift-cards", "gift-card"].includes(key)) return "gift-card";
  if (["telco-topup", "telco-topup-pin"].includes(key))
    return "telco-topup-pin";
  return key;
};

export default function GamesExplorer({
  games,
  compact = false,
  theme = "baasil",
}: {
  games: CatalogGameSummary[];
  compact?: boolean;
  theme?: "baasil" | "linzzo";
}) {
  const linzzo = theme === "linzzo";
  const [regionGame, setRegionGame] = useState<CatalogGameSummary | null>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const visibleGames = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return games
      .filter(
        (game) =>
          (category === "all" || categoryKey(game.category) === category) &&
          (!needle ||
            `${game.name} ${game.category || ""}`
              .toLowerCase()
              .includes(needle)),
      )
      .sort((left, right) => {
        const leftRank = featuredGameRank.get(left.slug);
        const rightRank = featuredGameRank.get(right.slug);
        if (leftRank !== undefined || rightRank !== undefined) {
          return (
            (leftRank ?? Number.MAX_SAFE_INTEGER) -
            (rightRank ?? Number.MAX_SAFE_INTEGER)
          );
        }
        return 0;
      });
  }, [category, games, query]);

  const openRegionSelector = (
    event: MouseEvent<HTMLAnchorElement>,
    game: CatalogGameSummary,
  ) => {
    if (validRegions(game).length <= 1) return;
    event.preventDefault();
    setRegionGame(game);
  };

  return (
    <>
      {!compact && !linzzo && (
        <div className="mb-10 flex flex-col items-center space-y-5">
          <div className="relative w-full max-w-lg">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search your game"
              className={`h-14 w-full rounded-2xl border pl-12 pr-4 font-semibold shadow-sm outline-none transition placeholder:font-medium ${linzzo ? "border-zinc-700 bg-zinc-950 text-white placeholder:text-zinc-500 focus:border-amber-400 focus:ring-4 focus:ring-amber-400/10" : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"}`}
            />
          </div>
          {!linzzo && (
            <div className="flex w-full max-w-full justify-start gap-2 overflow-x-auto pb-1 md:justify-center">
              {gameCategories.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setCategory(item.key)}
                  className={`shrink-0 rounded-full px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-wide transition ${category === item.key ? "bg-[#548DC8] text-white shadow-sm" : "border border-blue-100 bg-[#EFF6FF] text-[#1E40AF] hover:bg-blue-100"}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        className={
          compact
            ? "grid grid-cols-2 gap-3 md:grid-cols-4"
            : "flex flex-wrap justify-center gap-x-4 gap-y-8 sm:gap-x-6"
        }
      >
        {visibleGames.map((game) => (
          <Link
            key={game.id}
            href={`/games/${game.slug}`}
            onClick={(event) => openRegionSelector(event, game)}
            className={`group min-w-0 text-center ${compact ? "" : "w-[calc(50%-8px)] max-w-[190px] sm:w-[190px]"}`}
          >
            <div
              className={`relative aspect-square overflow-hidden rounded-[22px] border p-2 shadow-sm transition duration-300 group-hover:-translate-y-1 ${linzzo ? "border-zinc-800 bg-zinc-950 group-hover:border-amber-300/70 group-hover:shadow-[0_14px_30px_rgba(250,204,21,0.12)]" : "border-blue-100 bg-[#F7FAFF] group-hover:border-[#8DB9E5] group-hover:shadow-[0_14px_30px_rgba(84,141,200,0.16)]"}`}
            >
              {game.imageUrl ? (
                <img
                  src={catalogImageUrl(game.imageUrl)}
                  alt={game.imageAlt || `${game.name} top up`}
                  className="h-full w-full rounded-[1rem] object-cover transition duration-500 group-hover:scale-[1.04]"
                  loading="lazy"
                />
              ) : (
                <div
                  className={`flex h-full items-center justify-center rounded-[1rem] ${linzzo ? "bg-zinc-900" : "bg-blue-50"}`}
                >
                  <Gamepad2
                    className={`h-10 w-10 ${linzzo ? "text-amber-300" : "text-blue-300"}`}
                  />
                </div>
              )}
              <span className="absolute right-4 top-4 grid h-8 w-8 translate-y-1 place-items-center rounded-full bg-white/95 text-[#548DC8] opacity-0 shadow-md transition group-hover:translate-y-0 group-hover:opacity-100">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
            <div className="px-1 pb-2 pt-3">
              <h3
                className={`truncate font-display text-base font-normal uppercase leading-none tracking-wide sm:text-lg ${linzzo ? "text-white" : "text-[#343434]"}`}
              >
                {game.name}
              </h3>
              <div className="mt-2 flex items-center justify-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wide text-slate-400">
                <MapPin
                  className={`h-3 w-3 ${linzzo ? "text-amber-300" : "text-[#548DC8]"}`}
                />
                {validRegions(game).length > 1
                  ? `${validRegions(game).length} regions`
                  : getRegionDisplayName(
                      validRegions(game)[0]?.code,
                      validRegions(game)[0]?.name,
                    )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {!visibleGames.length && (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Gamepad2 className="mx-auto h-10 w-10 text-blue-300" />
          <p className="mt-3 font-bold text-slate-700">
            No matching games found.
          </p>
        </div>
      )}

      {regionGame && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="region-title"
        >
          <div
            className={`flex max-h-[min(720px,calc(100dvh-32px))] w-full max-w-2xl flex-col overflow-hidden rounded-3xl shadow-2xl ${linzzo ? "border border-amber-300/25 bg-zinc-950 text-white" : "bg-white"}`}
          >
            <div
              className={`flex shrink-0 items-start justify-between gap-4 border-b px-5 py-5 sm:px-6 ${linzzo ? "border-zinc-800" : "border-blue-50"}`}
            >
              <div>
                <p
                  className={`text-xs font-black uppercase tracking-[0.18em] ${linzzo ? "text-amber-300" : "text-blue-700"}`}
                >
                  Choose your region
                </p>
                <h2
                  id="region-title"
                  className={`mt-1 font-display text-2xl font-normal uppercase ${linzzo ? "text-white" : "text-[#343434]"}`}
                >
                  {regionGame.name}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Select the region connected to your game account.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close region selector"
                onClick={() => setRegionGame(null)}
                className="rounded-full border border-blue-100 bg-[#F7FAFF] p-2 text-[#548DC8] transition hover:bg-blue-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 overflow-y-auto p-4 sm:grid-cols-4 sm:gap-3 sm:p-6">
              {validRegions(regionGame).map((region) => (
                <Link
                  key={region.id}
                  href={
                    regionGame.slug === "free-fire" &&
                    isCombinedFreeFireRegion(regionGame, region)
                      ? "/games/free-fire?region=my-sg"
                      : `/games/${regionGame.slug}?region=${encodeURIComponent(region.slug)}`
                  }
                  onClick={() => {
                    localStorage.setItem(
                      `baasil-region:${regionGame.slug}`,
                      regionGame.slug === "free-fire" &&
                        isCombinedFreeFireRegion(regionGame, region)
                        ? "my-sg"
                        : region.slug,
                    );
                    setRegionGame(null);
                  }}
                  className={`group relative flex min-h-20 flex-col items-center justify-center rounded-2xl border px-2 py-3 text-center transition hover:-translate-y-0.5 hover:shadow-sm ${
                    linzzo
                      ? "border-zinc-800 bg-zinc-900 hover:border-amber-300/70"
                      : (
                            regionGame.slug === "free-fire"
                              ? isCombinedFreeFireRegion(regionGame, region)
                              : isSingaporeRegion(region)
                          )
                        ? "border-blue-300 bg-blue-50/70 hover:border-[#548DC8] hover:bg-blue-100/60"
                        : "border-blue-100 bg-[#F7FAFF] hover:border-[#548DC8] hover:bg-blue-50"
                  }`}
                >
                  <span
                    className="flex h-7 items-center justify-center"
                    aria-hidden="true"
                  >
                    <RegionIcons
                      code={
                        isCombinedFreeFireRegion(regionGame, region)
                          ? "SG/MY/LK"
                          : regionGame.slug === "free-fire" &&
                              region.code.toUpperCase() === "KH/SG/MY"
                            ? "KH"
                            : region.code
                      }
                    />
                  </span>
                  <span
                    className={`mt-2 font-display text-base font-normal uppercase leading-tight ${linzzo ? "text-white" : "text-[#343434]"}`}
                  >
                    {isCombinedFreeFireRegion(regionGame, region)
                      ? "SG/MY/LK"
                      : regionGame.slug === "free-fire" &&
                          region.code.toUpperCase() === "KH/SG/MY"
                        ? "Cambodia"
                        : getRegionDisplayName(region.code, region.name)}
                  </span>
                  {(regionGame.slug === "free-fire"
                    ? isCombinedFreeFireRegion(regionGame, region)
                    : isSingaporeRegion(region)) && (
                    <span
                      className={`mt-1 rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${linzzo ? "bg-amber-300 text-black" : "bg-blue-600 text-white"}`}
                    >
                      Recommended
                    </span>
                  )}
                </Link>
              ))}
            </div>
            <div className="shrink-0 border-t border-blue-50 bg-white px-5 py-4 text-center">
              <Link
                href={
                  regionGame.slug === "free-fire"
                    ? "/games/free-fire?region=my-sg"
                    : (() => {
                        const sg = (regionGame.regions || []).find((r) =>
                          isSingaporeRegion(r),
                        );
                        return sg?.slug
                          ? `/games/${regionGame.slug}?region=${encodeURIComponent(sg.slug)}`
                          : `/games/${regionGame.slug}`;
                      })()
                }
                className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-[#548DC8]"
              >
                View the general game page
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
